/*
  EXT-02 — a shim over Globo Form Builder's rendered fields.

  Globo prints its forms from JavaScript, so this markup does not exist in the theme and
  cannot be corrected at source. Three things it does not emit are needed for WCAG 2.2 AA,
  and none of them can be added with CSS because they are attributes:

    aria-required   Globo marks a required field with its own `presence` attribute and a
                    visual asterisk. The asterisk is in the accessible name, but screen
                    readers do not speak punctuation at default verbosity and the form
                    carries no "* = required" legend, so the requirement reaches sighted
                    users only. SC 3.3.2.

    autocomplete    The fields collect the visitor's own email and phone. SC 1.3.5
                    Identify Input Purpose requires the purpose to be programmatically
                    determinable, and the token is the way to do it.

    role="alert"    Validation messages are injected into <small class="messages">, which
                    is referenced by aria-describedby but carries no role - so an error
                    appears silently. SC 4.1.3.

  `type` is corrected too. Not a criterion, but `type="text"` on an email field means the
  wrong keyboard on a phone and no native validation.

  THIS IS A SHIM, NOT A FIX. It reads Globo's own `data-type` and `presence` attributes.
  If a vendor update renames them it stops working, and it stops working silently - which
  is why tests/a11y/content.spec.js asserts the resulting attributes rather than the
  presence of this file. Remove it once the vendor ships the same thing.
*/
(function () {
  const TOKENS = {
    email: { autocomplete: 'email', type: 'email' },
    phone: { autocomplete: 'tel', type: 'tel' },
    text: { autocomplete: 'name' },
  };

  function patchField(control) {
    const input = control.querySelector('input, textarea');
    if (!input || input.dataset.a11yPatched === 'true') return;

    const kind = control.getAttribute('data-type') || input.getAttribute('data-type');
    const token = TOKENS[kind];

    if (token) {
      if (!input.getAttribute('autocomplete')) input.setAttribute('autocomplete', token.autocomplete);
      // Only <input> has a type worth correcting, and only when Globo left it as text.
      if (token.type && input.tagName === 'INPUT' && input.type === 'text') {
        input.type = token.type;
      }
    }

    // Globo's own marker for "this field is required".
    if (input.hasAttribute('presence') && !input.hasAttribute('aria-required')) {
      input.setAttribute('aria-required', 'true');
    }

    /*
      Globo does not render one shape. The newsletter and popup forms give the field an
      `aria-describedby` pointing at `<small class="messages" id="...-error">`. The contact
      form emits the same `<small class="messages">` with NO id and NO aria-describedby, so
      its validation errors are neither announced nor attached to the field they describe
      (SC 3.3.1, 4.1.3). Measured on both.

      So the container is found by structure rather than by the reference, and the
      reference is created when it is missing.
    */
    const message = control.querySelector('small.messages');
    if (message) {
      // role="alert" rather than "status": a validation failure interrupts, and the
      // container is empty until one occurs, so there is nothing to announce on load.
      if (!message.getAttribute('role')) message.setAttribute('role', 'alert');

      if (!message.id) message.id = `${input.id || `globo-${Math.round(performance.now() * 1000)}`}-error`;
      const describedBy = input.getAttribute('aria-describedby') || '';
      if (!describedBy.split(/\s+/).includes(message.id)) {
        input.setAttribute('aria-describedby', `${describedBy} ${message.id}`.trim());
      }
    }

    input.dataset.a11yPatched = 'true';
  }

  function patchAll(root) {
    (root || document).querySelectorAll('.globo-form-control').forEach(patchField);
  }

  // The forms arrive after this script runs, and the popup builds its form later still,
  // so the observer is the mechanism rather than a fallback.
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.classList && node.classList.contains('globo-form-control')) patchField(node);
        else if (node.querySelector && node.querySelector('.globo-form-control')) patchAll(node);
      }
    }
  });

  const start = () => {
    patchAll(document);
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
